import { Link } from 'react-router-dom';
import type { Article } from '@/types/content';
import { formatDate, getCategory } from '@/utils/content';

type Variant = 'hero' | 'standard' | 'compact' | 'ranked';

interface ArticleCardProps {
  article: Article;
  variant?: Variant;
  rank?: number;
}

function CategoryLabel({ slug }: { slug: string }) {
  const category = getCategory(slug);
  if (!category) return null;
  return (
    <Link
      to={`/category/${category.slug}`}
      className="inline-flex items-center gap-1.5 font-label text-xs font-semibold uppercase tracking-[0.18em] text-primary-600 transition-colors hover:text-primary-700"
    >
      <span className="flex h-3.5 w-3.5 items-center justify-center">
        <i className={category.icon} />
      </span>
      {category.name}
    </Link>
  );
}

export default function ArticleCard({ article, variant = 'standard', rank }: ArticleCardProps) {
  if (variant === 'hero') {
    return (
      <article className="group relative overflow-hidden rounded-lg border border-background-300/70 bg-background-100">
        <Link to={`/article/${article.slug}`} className="block">
          <div className="relative h-56 w-full overflow-hidden sm:h-72 lg:h-80">
            <img
              src={article.cover}
              alt={article.coverAlt}
              className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/25 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
              <span className="inline-flex items-center rounded-full bg-accent-500 px-3 py-1 font-label text-xs font-semibold text-accent-950">
                本期重磅
              </span>
              <h3 className="mt-3 font-heading text-xl font-bold leading-snug text-background-50 md:text-2xl">
                {article.title}
              </h3>
              <p className="mt-2 hidden max-w-2xl text-sm leading-relaxed text-background-200 sm:block">
                {article.excerpt}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 font-label text-xs text-background-200">
                <span>{article.author}</span>
                <span>{formatDate(article.date)}</span>
                <span>约 {article.readTime} 分钟</span>
              </div>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  if (variant === 'compact') {
    return (
      <article className="group flex items-start gap-4 border-b border-background-300/70 pb-4 last:border-b-0 last:pb-0">
        <Link to={`/article/${article.slug}`} className="block w-24 shrink-0 sm:w-28">
          <div className="h-16 w-full overflow-hidden rounded-md sm:h-20">
            <img
              src={article.cover}
              alt={article.coverAlt}
              className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
            />
          </div>
        </Link>
        <div className="min-w-0">
          <h3 className="font-heading text-sm font-semibold leading-snug text-foreground-900 transition-colors group-hover:text-primary-600 md:text-base">
            <Link to={`/article/${article.slug}`}>{article.title}</Link>
          </h3>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 font-label text-xs text-foreground-500">
            <span className="text-primary-600">{getCategory(article.category)?.name}</span>
            <span>{formatDate(article.date)}</span>
          </div>
        </div>
      </article>
    );
  }

  if (variant === 'ranked') {
    return (
      <article className="group flex items-start gap-4 py-4">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-100 font-heading text-sm font-bold text-primary-700">
          {String(rank ?? 1).padStart(2, '0')}
        </span>
        <div className="min-w-0">
          <h3 className="font-heading text-sm font-semibold leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
            <Link to={`/article/${article.slug}`}>{article.title}</Link>
          </h3>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 font-label text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <span className="flex h-3.5 w-3.5 items-center justify-center text-accent-600">
                <i className="ri-fire-line" />
              </span>
              {article.heat.toLocaleString('zh-CN')}
            </span>
            <span>{getCategory(article.category)?.name}</span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-300/70 bg-background-100 transition-colors hover:border-primary-300">
      <Link to={`/article/${article.slug}`} className="block">
        <div className="h-44 w-full overflow-hidden md:h-48">
          <img
            src={article.cover}
            alt={article.coverAlt}
            className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
        </div>
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <CategoryLabel slug={article.category} />
        <h3 className="mt-2.5 font-heading text-base font-bold leading-snug text-foreground-950">
          <Link to={`/article/${article.slug}`} className="transition-colors hover:text-primary-600">
            {article.title}
          </Link>
        </h3>
        <p className="mt-2.5 line-clamp-3 text-sm leading-relaxed text-foreground-600">
          {article.excerpt}
        </p>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-background-200 pt-3 font-label text-xs text-foreground-500">
          <span>{article.author}</span>
          <span>{formatDate(article.date)}</span>
        </div>
      </div>
    </article>
  );
}